import { useOktaAuth } from '@okta/okta-react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';

import { clearState, loadState, saveState } from 'utils/storage';

import { AccountCompany } from 'contexts/account/types/account-company.type';
import { AccountUser } from 'contexts/account/types/account-user.type';
import { useThemeState } from 'contexts/theme/ThemeContext';
import { useGet } from 'hooks/use-get';
import { getCompanies } from './api/get-companies';
import { getCompany } from './api/get-company';
import { getCurrentUser } from './api/get-current-user';
import { setDatadogRumUser } from 'utils/datadog-rum';

enum actionTypes {
  SET_FETCHING = 'SET_FETCHING',
  SET_INITIALIZED = 'SET_INITIALIZED',
  SET_COMPANY = 'SET_COMPANY',
  SET_COMPANIES = 'SET_COMPANIES',
  SET_USERADMIN_USER = 'SET_USERADMIN_USER',
  SET_ADDRESS_ACCESS = 'SET_ADDRESS_ACCESS',
  RESET = 'RESET',
}

type State = {
  isFetching: boolean;
  isInitialized: boolean;
  company: AccountCompany | null;
  companies: AccountCompany[];
  currentUser: AccountUser | null;
};

type AccountState = {
  account: State;
  setAccount: (companyId: string) => void;
};

type Dispatch = (action: Action) => void;

type Action =
  | { type: actionTypes.SET_COMPANY; payload: AccountCompany | null }
  | { type: actionTypes.SET_COMPANIES; payload: AccountCompany[] }
  | { type: actionTypes.SET_USERADMIN_USER; payload: AccountUser }
  | { type: actionTypes.SET_INITIALIZED; payload: boolean }
  | { type: actionTypes.SET_FETCHING; payload: boolean }
  | { type: actionTypes.RESET };

const AccountStateContext = React.createContext<AccountState | undefined>(undefined);
const AccountDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const initialState: State = {
  isFetching: false,
  isInitialized: false,
  company: null,
  companies: [],
  currentUser: null,
};

function accountReducer(state: State, action: Action): State {
  switch (action.type) {
    case actionTypes.SET_FETCHING:
      return {
        ...state,
        isFetching: action.payload,
      };

    case actionTypes.SET_INITIALIZED:
      return {
        ...state,
        isInitialized: action.payload,
      };

    case actionTypes.SET_COMPANY:
      return {
        ...state,
        company: action.payload,
      };

    case actionTypes.SET_USERADMIN_USER:
      return {
        ...state,
        currentUser: action.payload,
      };

    case actionTypes.SET_COMPANIES:
      return {
        ...state,
        companies: action.payload,
      };

    case actionTypes.RESET:
      return initialState;

    default:
      return state;
  }
}

type AccountProviderProps = {
  children: React.ReactNode;
};

const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const { changeTheme } = useThemeState();
  const wasAuthenticated = useRef(false);
  // STATE
  const [companyId, setCompanyId] = useState<string>(loadState({ key: 'companyId' }));

  // FETCH DATA
  const { data: company, refetch: refetchCompany } = useGet(['getCompany', companyId], ({ queryKey }) => getCompany(String(queryKey[1])), {
    enabled: false,
  });

  const { data: companies } = useGet(['getCompanies', false], ({ queryKey }) => getCompanies(queryKey[1] as boolean), {
    enabled: !!state.isInitialized && !!authState?.isAuthenticated,
  });

  const { data: currentUser } = useGet(['getCurrentUser'], () => getCurrentUser(), {
    enabled: !!state.isInitialized && !!authState?.isAuthenticated,
  });

  // HANDLERS
  const setDatadogUser = (user?: AccountUser | null, company?: AccountCompany | null) => {
    if (!user || !company) return;

    setDatadogRumUser({
      id: user.id,
      name: user.firstName,
      companyId: company.companyId,
      companyType: company.companyType,
      marketId: company.marketId,
      userType: user.userType,
    });
  };

  const setAccount = useCallback((companyId: string) => {
    clearState({ key: 'cart' });
    dispatch({ type: actionTypes.SET_COMPANY, payload: null });
    dispatch({ type: actionTypes.SET_FETCHING, payload: true });
    setCompanyId(companyId);
  }, []);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      wasAuthenticated.current = true;
      return;
    }

    if (!wasAuthenticated.current) return;

    wasAuthenticated.current = false;
    setCompanyId('');
    dispatch({ type: actionTypes.RESET });
    delete axios.defaults.headers.common['x-market'];
    queryClient.clear();
  }, [authState?.isAuthenticated, queryClient]);

  useEffect(() => {
    const initAccount = () => {
      if (companyId) {
        setAccount(companyId);
      } else {
        dispatch({ type: actionTypes.SET_INITIALIZED, payload: true });
      }
    };

    if (!state.isInitialized && authState && authState.isAuthenticated) {
      initAccount();
    }
  }, [authState, companyId, oktaAuth, setAccount, state.isInitialized]);

  useEffect(() => {
    if (authState && authState.isAuthenticated && !!companyId) refetchCompany();
  }, [authState, companyId, refetchCompany]);

  useEffect(() => {
    if (!authState?.isAuthenticated || !company) return;

    axios.defaults.headers.common['x-market'] = `${company.marketId}`;
    saveState({ key: 'companyId', state: company.companyId });
    dispatch({ type: actionTypes.SET_COMPANY, payload: company });
    dispatch({ type: actionTypes.SET_INITIALIZED, payload: true });
    dispatch({ type: actionTypes.SET_FETCHING, payload: false });

    if (state.companies.length === 1 && state.companies[0] !== company)
      dispatch({
        type: actionTypes.SET_COMPANIES,
        payload: [company],
      }); //workaround to update companys' addressAccess
    changeTheme(company.aquaSpecies);
  }, [authState?.isAuthenticated, changeTheme, company, state.companies]);

  useEffect(() => {
    if (!authState?.isAuthenticated || !companies) return;

    dispatch({
      type: actionTypes.SET_COMPANIES,
      payload: [...companies].sort((a, b) => a.customerName.localeCompare(b.customerName)),
    });

    setDatadogUser(
      currentUser,
      companies.find((c) => c.companyId === companyId)
    );
  }, [authState?.isAuthenticated, companies]);

  useEffect(() => {
    if (!authState?.isAuthenticated || !currentUser) return;

    saveState({ key: 'userId', state: currentUser.id });

    dispatch({
      type: actionTypes.SET_USERADMIN_USER,
      payload: currentUser,
    });

    setDatadogUser(currentUser, company);
  }, [authState?.isAuthenticated, currentUser]);

  return (
    <AccountStateContext.Provider value={{ account: state, setAccount }}>
      <AccountDispatchContext.Provider value={dispatch}>{children}</AccountDispatchContext.Provider>
    </AccountStateContext.Provider>
  );
};

function useAccountState() {
  const context = useContext(AccountStateContext);
  if (context === undefined) {
    throw new Error('useAccountState must be used within a AccountProvider');
  }
  return context;
}

function useAccountDispatch() {
  const context = useContext(AccountDispatchContext);
  if (context === undefined) {
    throw new Error('useAccountDispatch must be used within a AccountProvider');
  }
  return context;
}

export { AccountProvider, useAccountDispatch, useAccountState };
